// Conflict Resolution — coordinators resolve has_conflict=true visit reports.

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";
import { Shell } from "../components/Shell.js";
import {
  ConflictCard,
  filterOpenConflicts,
  type ConflictReport,
  type ResolvePayload,
} from "../components/ConflictCard.js";

export function ConflictResolutionPage(): JSX.Element {
  const qc = useQueryClient();
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // /visit-reports returns { data, total, page, limit } — unwrap via select
  // so `reports` here is ConflictReport[] rather than the envelope object.
  // Without this, filterOpenConflicts(reports) and reports.length would
  // crash/mis-report at runtime even though useQuery<ConflictReport[]>
  // compiled cleanly.
  const { data: reports, isLoading, isError } = useQuery<
    { data: ConflictReport[]; total: number; page: number; limit: number },
    Error,
    ConflictReport[]
  >({
    queryKey: ["conflict-reports"],
    queryFn: () =>
      apiFetch<{ data: ConflictReport[]; total: number; page: number; limit: number }>(
        "/visit-reports?has_conflict=true&limit=50",
      ),
    select: (res) => res.data,
    refetchInterval: 60_000,
  });

  const resolve = useMutation({
    mutationFn: ({ report_id, field_name, winning_value }: ResolvePayload) =>
      apiFetch(`/visit-reports/${report_id}/conflicts/${encodeURIComponent(field_name)}`, {
        method: "PATCH",
        body: JSON.stringify({ winning_value }),
      }),
    onSuccess: (_data, payload) => {
      setSuccessMsg(`Conflict on "${payload.field_name}" resolved.`);
      void qc.invalidateQueries({ queryKey: ["conflict-reports"] });
      setTimeout(() => setSuccessMsg(null), 4000);
    },
  });

  const openConflicts = filterOpenConflicts(reports);
  const ready = !isLoading && !isError;
  const resolvedCount = (reports ? reports.length : 0) - openConflicts.length;

  return (
    <Shell>
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Conflict Resolution</h1>
          {!isLoading && (
            <p className="text-xs text-slate-500 mt-0.5">
              {openConflicts.length} open · {resolvedCount} resolved
            </p>
          )}
        </div>

        {successMsg && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2.5 text-sm text-green-700">
            {successMsg}
          </div>
        )}

        {isLoading && <p className="py-8 text-center text-sm text-slate-400">Loading…</p>}

        {isError && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-xl px-6 py-4">
            <p className="text-sm text-red-700 font-medium">Failed to load conflicts.</p>
          </div>
        )}

        {ready && openConflicts.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-sm text-slate-500">No open conflicts.</p>
          </div>
        )}

        {ready && openConflicts.map((report) => (
          <ConflictCard
            key={report._id}
            report={report}
            onResolve={(payload) => resolve.mutate(payload)}
          />
        ))}
      </div>
    </Shell>
  );
}
